/* @flow */

import * as React from 'react'
import styled, { css } from 'styled-components'
import isPlainObject from 'lodash/isPlainObject'
import debounce from 'lodash/debounce'

import { resolveCloudinaryResource } from '../../../../infrastructure/components/CloudinaryResource'
import {
  ControlLabel,
  FormGroup,
  FormInput,
} from '../../../../infrastructure/components/Formik'
import { addPxToInput } from '../../../shared'

export const WidgetContainer = React.forwardRef(
  ({ children, gridItem, isEditing, style = {}, className, ...rest }, ref) => {
    const combinedStyle = {
      ...style,
      ...generateGridItemContainerStyles(gridItem, isEditing),
    }

    return (
      <WidgetHtmlContainer
        {...rest}
        ref={ref}
        className={className}
        style={combinedStyle}
      >
        {children}
      </WidgetHtmlContainer>
    )
  }
)

export const generateGridItemContainerStyles = (gridItem, isEditing) => {
  const style = { ...(gridItem.style || {}) }

  for (let [k, v] of Object.entries(style)) {
    if (addPxSuffix.includes(k)) {
      style[k] = addPxToInput(v)
    }
  }

  return style
}

const addPxSuffix = [
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginTop',

  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
]

const WidgetHtmlContainer = styled.div``

type WebshopGridItemBannerProps = {
  bodyColor?: string,
  children: React.Node,
  contentContainerStyle: Object,
  gridItem: Object,
  heightMode: 'viewport_percentage' | 'keep_image_aspect_ratio' | 'fixed',
  heightValue: number,
  imageOrVideo: string | Array<{ path: string }> | null,
  isEditing?: boolean,
}

export const WebshopGridItemBanner = ({
  bodyColor,
  children,
  contentContainerStyle,
  gridItem,
  heightMode,
  heightValue,
  imageOrVideo,
  isEditing,
}: WebshopGridItemBannerProps) => {
  const containerRef = React.useRef(null)
  const containerSize = useContainerSize(containerRef)
  const cachedContainerSize =
    useSizeCacheClearningOnlyOnBiggerChanges(containerSize)

  const { imageUrl, videoSources } = useParsedMediaFile(
    imageOrVideo,
    cachedContainerSize
  )

  return (
    <Container
      ref={containerRef}
      gridItem={gridItem}
      heightMode={heightMode}
      heightValue={heightValue}
      bodyColor={bodyColor}
    >
      <BackgroundContainer heightMode={heightMode} heightValue={heightValue}>
        {videoSources !== null && (
          <VideoComponent
            autoPlay
            playsinline
            loop
            muted
            heightMode={heightMode}
            heightValue={heightValue}
          >
            {videoSources.map(source => (
              <source key={source.url} src={source.url} type={source.mime} />
            ))}
          </VideoComponent>
        )}
        {videoSources === null && imageUrl !== null && (
          <ImageComponent
            src={imageUrl}
            heightMode={heightMode}
            heightValue={heightValue}
          />
        )}
        {videoSources === null && imageUrl === null && isEditing && (
          <BackgroundPlaceholder
            heightMode={heightMode}
            heightValue={heightValue}
          />
        )}
      </BackgroundContainer>

      <ContentContainer style={contentContainerStyle}>
        {children}
      </ContentContainer>
    </Container>
  )
}

const useContainerSize = (containerRef: React.RefObject<HTMLElement>) => {
  const [containerWidth, setContainerWidth] = React.useState<number | null>(
    null
  )
  const [containerHeight, setContainerHeight] = React.useState<number | null>(
    null
  )

  const checkContainerSize = React.useCallback(
    // Debounce so it gets called only once after the last resize event
    debounce(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth)
        setContainerHeight(containerRef.current.clientHeight)
      }
    }, 100),
    [setContainerWidth, setContainerHeight]
  )

  React.useEffect(() => {
    checkContainerSize()
    window.addEventListener('resize', checkContainerSize)
    return () => {
      window.removeEventListener('resize', checkContainerSize)
    }
  }, [checkContainerSize])

  const containerSize = React.useMemo(
    () =>
      containerWidth && containerHeight
        ? { width: containerWidth, height: containerHeight }
        : null,
    [containerWidth, containerHeight]
  )

  return containerSize
}

const useSizeCacheClearningOnlyOnBiggerChanges = (
  size: { width: number, height: number } | null
) => {
  const [cachedSize, setCachedSize] = React.useState<{
    width: number,
    height: number,
  } | null>(size)

  const clearingThresholdInPx = 200

  React.useEffect(() => {
    if (size === null) {
      return
    }

    if (cachedSize === null) {
      setCachedSize(size)
      return
    }

    const widthDiff = Math.abs(size.width - cachedSize.width)
    const heightDiff = Math.abs(size.height - cachedSize.height)

    if (
      widthDiff > clearingThresholdInPx ||
      heightDiff > clearingThresholdInPx
    ) {
      setCachedSize(size)
    }
  }, [size])

  return cachedSize
}

const useParsedMediaFile = (
  file: string | Array<{ path: string }> | null,
  containerSize: { width: number, height: number } | null
) => {
  return React.useMemo(() => {
    if (!file || !containerSize) {
      return {
        imageUrl: null,
        videoSources: null,
      }
    }

    // Temp uploaded file is an array with one file object
    const isTempUploadedFile =
      Array.isArray(file) &&
      file.length === 1 &&
      isPlainObject(file[0]) &&
      typeof file[0].path === 'string'

    let cloudinaryId

    if (typeof file === 'string') {
      cloudinaryId = file
    } else if (isTempUploadedFile) {
      cloudinaryId = file[0].path
    } else {
      // If it's not detected as valid cloudinary id or temp uploaded file,
      // then just dont show anything
      return {
        imageUrl: null,
        videoSources: null,
      }
    }

    const isVideo = cloudinaryId.startsWith('video/')

    if (isVideo) {
      // We dont want any transformations based on width/height for video,
      // because they take too long to load then
      const cloudinaryParams = {
        id: cloudinaryId,
        format: 'mp4',
      }

      const { url } = resolveCloudinaryResource(cloudinaryParams)

      return {
        imageUrl: null,
        videoSources: [
          {
            url,
            mime: 'video/mp4',
          },
        ],
      }
    } else {
      const cloudinaryParams = {
        id: cloudinaryId,
        format: 'jpeg',
        presets: ['webshop_landing_image'],
        transformations: {
          width: containerSize.width,
          height: containerSize.height,
        },
      }

      const { url } = resolveCloudinaryResource(cloudinaryParams)

      return {
        imageUrl: url,
        videoSources: null,
      }
    }
  }, [file, containerSize])
}

const getHeightCss = ({ heightMode, heightValue }) => {
  if (heightMode === 'viewport_percentage') {
    return css`
      height: ${heightValue}vh;
    `
  }

  if (heightMode === 'fixed') {
    return css`
      height: ${addPxToInput(heightValue)};
    `
  }

  return ''
}

const Container = styled(WidgetContainer)`
  color: ${({ bodyColor }) => bodyColor || 'inherit'};
  position: relative;

  ${props => getHeightCss(props)}
`

const BackgroundContainer = styled.div`
  top: 0;
  left: 0;
  right: 0;
  min-width: 100%;
  position: absolute;
  z-index: -100;

  ${props => getHeightCss(props)}
`

const VideoComponent = styled.video`
  display: block;
  width: 100%;
  top: 50%;
  object-fit: cover;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -50%);

  ${props => getHeightCss(props)}
`

const ImageComponent = styled.img`
  display: block;
  top: 50%;
  object-fit: cover;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -50%);
  width: 100%;

  ${props => getHeightCss(props)}
`

const BackgroundPlaceholder = styled.div`
  width: 100%;
  background-color: rgba(213, 213, 213, 0.8);

  ${props => getHeightCss(props)}
`

const ContentContainer = styled.div`
  position: relative;
`

export const SettingsSeparator = styled.hr`
  border: none;
  border-top: 2px solid #d9d9d9;
  margin: 20px -10px 20px -10px;
`

export const SpacingSettings = ({
  enabledSpacingKeys = [
    'paddingLeft',
    'paddingRight',
    'paddingTop',
    'paddingBottom',
    'marginLeft',
    'marginRight',
    'marginTop',
    'marginBottom',
  ],
}: {
  enabledSpacingKeys?: (
    | 'paddingLeft'
    | 'paddingRight'
    | 'paddingTop'
    | 'paddingBottom'
    | 'marginLeft'
    | 'marginRight'
    | 'marginTop'
    | 'marginBottom'
  )[],
}) => {
  return (
    <div>
      {['paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom'].some(
        spacingKey => enabledSpacingKeys.includes(spacingKey)
      ) && (
        <FormGroup>
          <ControlLabel>Padding (inner spacing)</ControlLabel>
          <SpacingInputsContainer>
            {['paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom']
              .filter(spacingKey => enabledSpacingKeys.includes(spacingKey))
              .map(spacingKey => (
                <SpacingInputCellContainer key={spacingKey}>
                  <SpacingInputIcon spacingKey={spacingKey} />
                  <FormInput
                    name={`style.${spacingKey}`}
                    type="number"
                    min={0}
                  />
                  <SpacingInputPostfix>px</SpacingInputPostfix>
                </SpacingInputCellContainer>
              ))}
          </SpacingInputsContainer>
        </FormGroup>
      )}
      {['marginLeft', 'marginRight', 'marginTop', 'marginBottom'].some(
        spacingKey => enabledSpacingKeys.includes(spacingKey)
      ) && (
        <FormGroup>
          <ControlLabel>Margin (outer spacing)</ControlLabel>
          <SpacingInputsContainer>
            {['marginLeft', 'marginRight', 'marginTop', 'marginBottom']
              .filter(spacingKey => enabledSpacingKeys.includes(spacingKey))
              .map(spacingKey => (
                <SpacingInputCellContainer key={spacingKey}>
                  <SpacingInputIcon spacingKey={spacingKey} />
                  <FormInput
                    name={`style.${spacingKey}`}
                    type="number"
                    min={0}
                  />
                  <SpacingInputPostfix>px</SpacingInputPostfix>
                </SpacingInputCellContainer>
              ))}
          </SpacingInputsContainer>
        </FormGroup>
      )}
    </div>
  )
}

const SpacingInputsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  row-gap: 7px;
`

const SpacingInputCellContainer = styled.div`
  display: flex;
  align-items: center;
`

const SpacingInputIcon = ({ spacingKey }: { spacingKey: string }) => {
  return (
    <SpacingInputIconSvg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        y="4"
        width="4"
        height="12"
        fill={spacingKey.endsWith('Left') ? 'black' : '#D7D7D7'}
      />
      <rect
        x="16"
        y="4"
        width="4"
        height="12"
        fill={spacingKey.endsWith('Right') ? 'black' : '#D7D7D7'}
      />
      <rect
        x="4"
        width="12"
        height="4"
        fill={spacingKey.endsWith('Top') ? 'black' : '#D7D7D7'}
      />
      <rect
        x="4"
        y="16"
        width="12"
        height="4"
        fill={spacingKey.endsWith('Bottom') ? 'black' : '#D7D7D7'}
      />
    </SpacingInputIconSvg>
  )
}

const SpacingInputIconSvg = styled.svg`
  flex: 0 0 auto;
  margin-right: 7px;
`

const SpacingInputPostfix = styled.div`
  margin-left: 7px;
  margin-right: 20px;
`
